export interface OutgoingCallRingtoneAudio {
  loop: boolean
  preload: string
  currentTime: number
  play: () => Promise<void> | void
  pause: () => void
}

interface OutgoingCallRingtoneOptions {
  createAudio?: (url: string) => OutgoingCallRingtoneAudio
  logger?: Pick<Console, 'warn'>
  url?: string
}

function publicAssetUrl(path: string) {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/?$/, '/')
  return `${base}${path.replace(/^\//, '')}`
}

export const OUTGOING_CALL_RINGTONE_URL = publicAssetUrl('audio/video_call_outgoing.mp3')

export function createOutgoingCallRingtonePlayer(options: OutgoingCallRingtoneOptions = {}) {
  const createAudio = options.createAudio || ((url: string) => new Audio(url))
  const logger = options.logger || console
  const ringtoneUrl = options.url || OUTGOING_CALL_RINGTONE_URL
  let activeOrderID: string | null = null
  let audio: OutgoingCallRingtoneAudio | null = null

  const stopAudio = (target: OutgoingCallRingtoneAudio | null) => {
    if (!target) {
      return
    }
    try {
      target.pause()
    } catch {
      // Ignore browser audio cleanup failures.
    }
    try {
      target.currentTime = 0
    } catch {
      // Some browser audio objects may reject currentTime before metadata loads.
    }
  }

  return {
    start(orderID: string | null | undefined) {
      if (!orderID) {
        this.stop()
        return
      }
      if (activeOrderID === orderID && audio) {
        return
      }
      this.stop()
      const nextAudio = createAudio(ringtoneUrl)
      nextAudio.loop = true
      nextAudio.preload = 'auto'
      activeOrderID = orderID
      audio = nextAudio
      const playResult = nextAudio.play()
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch((error) => {
          logger.warn('Outgoing call ringtone playback was blocked.', error)
        })
      }
    },
    stop(orderID?: string | null) {
      if (orderID && activeOrderID !== orderID) {
        return
      }
      stopAudio(audio)
      audio = null
      activeOrderID = null
    },
    destroy() {
      this.stop()
    },
    getActiveOrderID() {
      return activeOrderID
    },
  }
}
